import React, { useState, useEffect, useRef } from "react"
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material"
import BluetoothIcon from "@mui/icons-material/Bluetooth"
import { useParams, useNavigate } from "react-router-dom"
import { useQuery, useQueryClient } from "react-query"
import BoardCanvas from "../components/BoardCanvas"
import useStickyState from "../hooks/useStickyState"
import {
  fetchGym,
  createGym,
  clearGymSlot,
  NotFoundError,
} from "../utils/gymApi"
import {
  connectToBluetooth,
  sendClimbsToWall,
  clearWall,
  PALETTE_A,
  PALETTE_B,
} from "../utils/bluetooth"

const SLOT_PALETTES = { A: PALETTE_A, B: PALETTE_B }
const SLOT_COLOR_HINTS = {
  A: "green start / cyan middle / magenta finish",
  B: "blue start / yellow middle / white finish",
}

const gradeAtAngle = (climb, angle) => {
  if (!climb?.stats?.length) {
    return null
  }
  const stat =
    climb.stats.find((s) => s.angle === angle) || climb.stats[0]
  return `${stat.display_difficulty} @ ${stat.angle}°`
}

function SlotCard({ slug, slot, data, onStateChange }) {
  const navigate = useNavigate()

  const handleClear = async () => {
    const state = await clearGymSlot(slug, slot)
    onStateChange(state)
  }

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Problem {slot}
          <Typography
            component="span"
            variant="caption"
            color="text.secondary"
            sx={{ ml: 1 }}
          >
            {SLOT_COLOR_HINTS[slot]}
          </Typography>
        </Typography>
        {data ? (
          <>
            <Typography variant="subtitle1">
              {data.climb?.name || data.name || "Custom selection"}
              {data.climb?.setter_username &&
                ` by ${data.climb.setter_username}`}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {gradeAtAngle(data.climb, data.angle)}
            </Typography>
            {data.error === "climb_not_found" ? (
              <Typography color="error" gutterBottom>
                This climb is no longer in the database.
              </Typography>
            ) : (
              <BoardCanvas climb={data.climb} />
            )}
          </>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "20vh",
            }}
          >
            <Typography color="text.secondary">Empty</Typography>
          </Box>
        )}
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              navigate(`/search-climbs?gym=${slug}&slot=${slot}`)
            }
          >
            Pick climb
          </Button>
          {data && (
            <Button
              variant="outlined"
              size="small"
              color="warning"
              onClick={handleClear}
            >
              Clear
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

function CreateGymPrompt({ slug, onCreated }) {
  const [name, setName] = useState("")
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    setIsCreating(true)
    try {
      const state = await createGym(slug, name || slug)
      onCreated(state)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Card variant="outlined" sx={{ maxWidth: 480, mx: "auto", mt: 4 }}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          No gym here yet
        </Typography>
        <Typography color="text.secondary" gutterBottom>
          Create a page for the kilter board at <b>{slug}</b>? Anyone with
          this link will see what's currently on the wall.
        </Typography>
        <TextField
          fullWidth
          size="small"
          label="Display name (optional)"
          value={name}
          onChange={(event) => setName(event.target.value)}
          sx={{ my: 2 }}
        />
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={isCreating}
        >
          Create {slug}
        </Button>
      </CardContent>
    </Card>
  )
}

export default function Gym() {
  const { slug } = useParams()
  const queryClient = useQueryClient()
  const [bluetoothCharacteristic, setBluetoothCharacteristic] = useState(null)
  const [recentGyms, setRecentGyms] = useStickyState([], "recentGyms")
  const lastSentRef = useRef(null)

  const { data: gym, error, isLoading } = useQuery(
    ["gym", slug],
    () => fetchGym(slug),
    { refetchInterval: 4000, refetchIntervalInBackground: true, retry: false }
  )

  const setGymState = (state) => queryClient.setQueryData(["gym", slug], state)

  useEffect(() => {
    if (!gym) {
      return
    }
    const entry = { slug: gym.slug, name: gym.name }
    setRecentGyms((recents) =>
      [entry, ...(recents || []).filter((g) => g.slug !== gym.slug)].slice(0, 5)
    )
  }, [gym?.slug, gym?.name])

  const slotEntries = ["A", "B"]
    .map((slot) => ({
      climb: gym?.slots?.[slot]?.climb,
      palette: SLOT_PALETTES[slot],
    }))
    .filter((entry) => entry.climb)

  const updatedKey = `${gym?.slots?.A?.updated_at}|${gym?.slots?.B?.updated_at}`

  const sendToBoard = async () => {
    if (!bluetoothCharacteristic) {
      return
    }
    if (slotEntries.length === 0) {
      await clearWall(bluetoothCharacteristic)
    } else {
      await sendClimbsToWall(bluetoothCharacteristic, slotEntries)
    }
    lastSentRef.current = updatedKey
  }

  useEffect(() => {
    if (bluetoothCharacteristic && gym && lastSentRef.current !== updatedKey) {
      sendToBoard()
    }
  }, [bluetoothCharacteristic, updatedKey, gym])

  const handleConnectToBluetooth = async () => {
    try {
      const characteristic = await connectToBluetooth()
      setBluetoothCharacteristic(characteristic)
    } catch (error) {
      console.error("Error connecting to Bluetooth:", error)
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error instanceof NotFoundError) {
    return <CreateGymPrompt slug={slug} onCreated={setGymState} />
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 6 }}>
        Could not load this gym. Is the server running?
      </Typography>
    )
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h4" align="center" gutterBottom>
          {gym.name}
        </Typography>
        <Box
          sx={{ display: "flex", justifyContent: "center", gap: 1, mb: 1 }}
        >
          <Button
            variant={bluetoothCharacteristic ? "contained" : "outlined"}
            size="small"
            startIcon={<BluetoothIcon />}
            onClick={handleConnectToBluetooth}
          >
            {bluetoothCharacteristic ? "Connected" : "Connect to board"}
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!bluetoothCharacteristic || slotEntries.length === 0}
            onClick={sendToBoard}
          >
            Send to board
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="warning"
            disabled={!bluetoothCharacteristic}
            onClick={() => clearWall(bluetoothCharacteristic)}
          >
            Blank board
          </Button>
        </Box>
      </Grid>
      <Grid item xs={12} md={6}>
        <SlotCard
          slug={slug}
          slot="A"
          data={gym.slots?.A}
          onStateChange={setGymState}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SlotCard
          slug={slug}
          slot="B"
          data={gym.slots?.B}
          onStateChange={setGymState}
        />
      </Grid>
    </Grid>
  )
}
